/** @jsxImportSource @emotion/react */
import { useState } from 'react';
import { PropTypes } from 'prop-types';
import { useTheme } from '@mui/material';
import { css } from '@emotion/react';
import { Field } from 'formik';
import useStyle from './style';
import passwordHidden from '../../../svgs/passwordHidden.svg';
import passwordVisible from '../../../svgs/passwordVisible.svg';
import SmoothMount from '../../../UI/SmoothMount';

const TextInput = ({
  id = '',
  name = '',
  placeholder = '',
  label = '',
  size = 'md',
  isProtected = false,
  showErrorBelowInput = false,
  formatInput = null,
  type = 'text',
  showLabel = false,
  validateOnFocus = false,
  inputWrapperCss = css``,
  questionToolTip = null,
  disabled = false,
  secure = false,
  ...props
}) => {
  const {
    inputWrapper,
    input,
    errorInput,
    errorMessage,
    hidden,
    showLabelStyles,
    showLabelInputStyle,
    inputEndActions,
    inputEndAction,
    questionToolTipStyle,
  } = useStyle();
  const [showProtectedField, setShowProtectedField] = useState(false);
  const theme = useTheme();
  const { sm } = theme.breakpoints;
  const sizes = {
    sm: '290px',
    md: '458px',
    lg: '719px',
  };

  return (
    <Field id={id} name={name} {...props}>
      {({ field, form: { touched, errors, setTouched } }) => {
        const hasError = touched[name] && errors[name];
        const onFocus = () => {
          setTouched({ ...touched, [field.name]: true });
        };
        return (
          <div
            css={css`
              min-width: ${sizes[size] || ''};
              ${inputWrapper};

              @media (max-width: ${`${sm}px`}) {
                min-width: unset;
                width: 100%;
                margin: 0 0 15px;
                padding: 0;
              }
              ${inputWrapperCss}
            `}
          >
            <input
              id={id}
              name={name}
              placeholder={showLabel ? ' ' : placeholder}
              css={css`
                ${input}
                ${hasError && errorInput}
                ${showLabel ? showLabelInputStyle : ''}
              `}
              onChange={e => {
                if (formatInput) {
                  e.target.value = formatInput(e.target.value);
                }
                field.onChange(e);
              }}
              type={isProtected && !showProtectedField ? 'password' : type}
              onBlur={e => {
                if (props.onBlur) {
                  e.target.value = props.onBlur(e.target.value);
                }
                field.onBlur(e);
              }}
              onKeyPress={e => {
                if (props.onKeyPress) {
                  props.onKeyPress(e);
                }
              }}
              onFocus={validateOnFocus ? onFocus : () => {}}
              disabled={disabled ? 'disabled' : ''}
              {...field}
              {...props}
            />
            <label
              css={css`
                ${showLabel ? showLabelStyles : hidden}
              `}
              htmlFor={id || name}
            >
              {label}
            </label>
            <span css={inputEndActions}>
              {isProtected && (
                <span
                  css={inputEndAction}
                  onClick={() => setShowProtectedField(!showProtectedField)}
                >
                  {showProtectedField ? (
                    <img src={passwordVisible} />
                  ) : (
                    <img src={passwordHidden} />
                  )}
                </span>
              )}
              {questionToolTip && (
                <span css={inputEndAction}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="34"
                    height="34"
                    viewBox="0 0 34 34"
                    fill="none"
                  >
                    <circle cx="17" cy="17" r="6" fill="#515151" />
                    <path
                      d="M16.528 18.408H17.416V18.176C17.416 17.048 18.96 17.16 18.96 15.776C18.96 14.824 18.208 14.32 17.144 14.32C16.232 14.32 15.512 14.808 15.192 15.432L15.856 15.896C16.136 15.432 16.528 15.112 17.12 15.112C17.704 15.112 18.064 15.376 18.064 15.808C18.064 16.72 16.528 16.688 16.528 18.104V18.408ZM16.496 20H17.456V19H16.496V20Z"
                      fill="white"
                    />
                  </svg>
                  <div css={questionToolTipStyle}>{questionToolTip}</div>
                </span>
              )}
              {secure && (
                <span css={inputEndAction}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="34"
                    height="34"
                    viewBox="0 0 34 34"
                    fill="none"
                  >
                    <rect x="12" y="14" width="12" height="9" fill="#515151" />
                    <rect
                      x="15"
                      y="10"
                      width="6"
                      height="10"
                      rx="3"
                      stroke="#515151"
                      strokeWidth="2"
                    />
                    <rect x="17" y="16" width="2" height="5" fill="white" />
                  </svg>
                </span>
              )}
            </span>
            <SmoothMount show={hasError && showErrorBelowInput}>
              <div css={errorMessage}>{errors[name]}</div>
            </SmoothMount>
          </div>
        );
      }}
    </Field>
  );
};

TextInput.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  size: PropTypes.string,
  isProtected: PropTypes.bool,
  showErrorBelowInput: PropTypes.bool,
  formatInput: PropTypes.func,
  type: PropTypes.string,
  showLabel: PropTypes.bool,
  validateOnFocus: PropTypes.bool,
  inputWrapperCss: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  questionToolTip: PropTypes.node,
  disabled: PropTypes.bool,
  secure: PropTypes.bool,
};

export default TextInput;
